import { ReactNode, createContext } from 'react';
import { usePathname, useSearchParams, useRouter } from 'next/navigation';
import React, { useState } from 'react';

type paginationType = {
  page?: string | number;
  pageSize?: string | number;
  view?: string;
  sortBy?: string;
  sortDirection?: string;
};

export type URLContextType<T> = {
  searchQueryVal: T & paginationType;
  locationParams: any;
  locationParamsObj: T & paginationType;
  onChangeFormFilterQuery: (name: string, value: any, isApply?: boolean) => void;
  onChangeBulkQuery: (qry: any) => void;
  resetFormFilterQuery: (qry?: any) => void;
  applyQuery: (callback?: any) => void;
};

export const URLSearchContext = createContext<URLContextType<any>>({
  searchQueryVal: {},
  locationParams: {},
  locationParamsObj: {},
  onChangeFormFilterQuery: () => {},
  onChangeBulkQuery: () => {},
  resetFormFilterQuery: () => {},
  applyQuery: () => {},
});

export const URLSearchProvider = ({ children }: { children: ReactNode }) => {
  const [searchQueryVal, setSearchQueryVal] = useState<any>({});

  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const locationParams = new URLSearchParams(searchParams);
  const locationParamsObj = Object.fromEntries(new URLSearchParams(locationParams));

  React.useEffect(() => {
    setSearchQueryVal(locationParamsObj);
  }, [pathname]);

  const onChangeFormFilterQuery = (name: string, value: any, isApply?: boolean) => {
    const updatedQuery = { ...searchQueryVal, [name]: value };
    setSearchQueryVal(updatedQuery);
    if (isApply) {
      const params = new URLSearchParams(updatedQuery);
      const queryString = params.toString();
      const updatedPath = queryString ? `${pathname}?${queryString}` : pathname;
      router.push(updatedPath);
    }
  };

  const onChangeBulkQuery = (qry: any) => {
    setSearchQueryVal({ ...searchQueryVal, ...qry });
    updateSearchQuery(qry);
  };

  const applyQuery = (callback: any) => {
    updateSearchQuery(searchQueryVal);
    callback?.(searchQueryVal);
  };

  const updateSearchQuery = (updatedQuery: any) => {
    const params = new URLSearchParams({ ...searchQueryVal, ...updatedQuery });
    Object.keys(updatedQuery).forEach((key) => {
      if (updatedQuery[key]) {
        params.set(key, updatedQuery[key]);
      } else {
        params.delete(key);
      }
    });
    const queryString = params.toString();
    const updatedPath = queryString ? `${pathname}?${queryString}` : pathname;
    router.push(updatedPath);
  };

  const resetFormFilterQuery = (additionalParams?: any) => {
    let params = {} as any;

    if (searchQueryVal) {
      if (additionalParams && !additionalParams?.target) {
        params = { ...params, ...additionalParams };
      }
      if (searchQueryVal.tab && !additionalParams?.tab) {
        params.tab = searchQueryVal.tab;
      }
      if (searchQueryVal.page) {
        params.page = searchQueryVal.page || '1';
        params.pageSize = searchQueryVal.pageSize || '10';
      }
      if (searchQueryVal.sortBy) {
        params.sortBy = searchQueryVal.sortBy;
        params.sortDirection = searchQueryVal.sortDirection;
      }
      if (searchQueryVal.view) {
        params.view = searchQueryVal.view;
      }
    }

    const updatedPath = `${pathname}?${new URLSearchParams(params).toString()}`;
    router.replace(updatedPath);
    setSearchQueryVal(params);
  };

  return (
    <URLSearchContext.Provider
      value={{
        searchQueryVal,
        locationParams,
        locationParamsObj,
        onChangeFormFilterQuery,
        onChangeBulkQuery,
        resetFormFilterQuery,
        applyQuery,
      }}
    >
      {children}
    </URLSearchContext.Provider>
  );
};
